"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import { useRealtimeChannel } from "@/lib/realtime/useRealtimeChannel";
import {
  dispatchSupportUnreadRefresh,
  fetchSupportUnreadCount,
} from "@/lib/support/unread";

type SupportUnreadContextValue = {
  count: number;
  ready: boolean;
  reconcile: () => Promise<void>;
};

const SupportUnreadContext = createContext<SupportUnreadContextValue>({
  count: 0,
  ready: false,
  reconcile: async () => {},
});

export function useSupportUnread() {
  return useContext(SupportUnreadContext);
}

export default function SupportUnreadProvider({
  audience,
  enabled = true,
  children,
}: {
  audience: "customer" | "business";
  enabled?: boolean;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const { supabase, user, authStatus } = useAuth();
  const [count, setCount] = useState(0);
  const [ready, setReady] = useState(false);
  const userId = user?.id || null;
  const active = enabled && authStatus === "authenticated" && Boolean(userId) && Boolean(supabase);

  const reconcile = useCallback(async () => {
    if (!active || !supabase) {
      setCount(0);
      setReady(false);
      return;
    }
    try {
      setCount(await fetchSupportUnreadCount(supabase, audience));
      setReady(true);
    } catch (error) {
      console.warn("Failed to reconcile unread Support conversations", error);
    }
  }, [active, audience, supabase]);

  useEffect(() => {
    const timer = window.setTimeout(() => { void reconcile(); }, 0);
    return () => window.clearTimeout(timer);
  }, [pathname, reconcile]);

  useEffect(() => {
    if (!active || typeof window === "undefined") return undefined;
    const handleFocus = () => {
      void reconcile();
      dispatchSupportUnreadRefresh({ kind: "reconcile" });
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") handleFocus();
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [active, reconcile]);

  const buildChannel = useCallback((client: any) => client
    .channel(`support-unread-${audience}-${userId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "support_requester_events",
        filter: `audience=eq.${audience}`,
      },
      (payload: any) => {
        const message = payload?.new;
        if (message?.audience !== audience) return;
        void reconcile();
        dispatchSupportUnreadRefresh({ kind: "message", ticketId: message.ticket_id || null });
      }
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "support_ticket_reads",
        filter: `user_id=eq.${userId}`,
      },
      (payload: any) => {
        void reconcile();
        dispatchSupportUnreadRefresh({
          kind: "read",
          ticketId: payload?.new?.ticket_id || payload?.old?.ticket_id || null,
        });
      }
    ), [audience, reconcile, userId]);

  const handleStatus = useCallback((status: string) => {
    if (status === "SUBSCRIBED") {
      void reconcile();
      dispatchSupportUnreadRefresh({ kind: "reconcile" });
    }
  }, [reconcile]);

  useRealtimeChannel({
    supabase,
    enabled: active,
    buildChannel,
    diagLabel: `support-unread-${audience}`,
    onStatus: handleStatus,
  });

  const value = useMemo(() => ({ count, ready, reconcile }), [count, ready, reconcile]);
  return <SupportUnreadContext.Provider value={value}>{children}</SupportUnreadContext.Provider>;
}
